/**
 * Shared content workflow statuses for all publishable CMS collections.
 * Live site rebuild happens only via the global Publish action — never on status save.
 *
 * Automated Data Tools (fix hierarchy, seed, translations, images) demote
 * Live / Live · Ready → Live · Draft. Editors mark Live · Ready, then Publish.
 * Manual CMS edits of live docs also become Live · Draft; editors mark Live · Ready.
 */
import type { EnumValues } from "@firecms/core";
import type { ChipColorKey, ChipColorScheme } from "@firecms/ui";

/**
 * Status labels (consistent “Live” naming for on-site content):
 * - Live = on site and in sync with CMS
 * - Live · Draft = on site; CMS has unpublished edits
 * - Live · Ready = on site; queued for the next Publish to update live content
 */
export const CONTENT_STATUS = {
  draft: "Draft",
  ready_for_publishing: "Ready for Publishing",
  published: "Live",
  published_draft: "Live · Draft",
  published_ready: "Live · Ready",
  unpublishing: "Unpublishing",
  unpublished: "Unpublished",
  archived: "Archived",
} as const;

export type ContentStatus = keyof typeof CONTENT_STATUS;

export const CONTENT_STATUS_VALUES = Object.keys(CONTENT_STATUS) as ContentStatus[];

/** Chip colors for FireCMS status enums (readable on light and dark surfaces). */
const CONTENT_STATUS_COLORS: Record<ContentStatus, ChipColorKey | ChipColorScheme> = {
  draft: "grayLight",
  ready_for_publishing: "cyanLight",
  published: "greenDark",
  published_draft: { color: "#F59E0B", text: "#111827" },
  published_ready: { color: "#38BDF8", text: "#0F172A" },
  unpublishing: "orangeLight",
  unpublished: "grayLighter",
  archived: { color: "#374151", text: "#F3F4F6" },
};

/** Use as `enumValues` on status fields so chips get explicit colors. */
export const CONTENT_STATUS_ENUM: EnumValues = (
  Object.keys(CONTENT_STATUS) as ContentStatus[]
).map((id) => ({
  id,
  label: CONTENT_STATUS[id],
  color: CONTENT_STATUS_COLORS[id],
}));

/** Statuses editors may set in the form (Live / Unpublished are system-only). */
export const EDITOR_SETTABLE_STATUSES: ReadonlySet<ContentStatus> = new Set([
  "draft",
  "ready_for_publishing",
  "published_draft",
  "published_ready",
  "unpublishing",
  "archived",
]);

/** First-time publish queue (never been live, or back from unpublished). */
export const PUBLISH_QUEUE_STATUS = "ready_for_publishing" as const;

/** Update-live queue: already on site, CMS edits staged for next Publish. */
export const LIVE_READY_STATUS = "published_ready" as const;

/** Statuses the Publish button promotes to Live. */
export const PUBLISH_QUEUE_STATUSES: readonly ContentStatus[] = [
  PUBLISH_QUEUE_STATUS,
  LIVE_READY_STATUS,
];

export const UNPUBLISH_QUEUE_STATUS = "unpublishing" as const;

/** Still served on the live site (from published_snapshot when not pure Live). */
export const LIVE_ON_SITE_STATUSES: ReadonlySet<ContentStatus> = new Set([
  "published",
  "published_draft",
  "published_ready",
]);

const ALLOWED_TRANSITIONS: Record<ContentStatus, ReadonlySet<ContentStatus>> = {
  draft: new Set(["draft", "ready_for_publishing", "archived"]),
  ready_for_publishing: new Set(["ready_for_publishing", "draft", "archived"]),
  published: new Set([
    "published",
    "published_draft",
    "published_ready",
    "unpublishing",
    "archived",
  ]),
  published_draft: new Set([
    "published_draft",
    "published_ready",
    "unpublishing",
    "archived",
  ]),
  published_ready: new Set([
    "published_ready",
    "published_draft",
    "unpublishing",
    "archived",
  ]),
  unpublishing: new Set(["unpublishing", "published", "archived"]),
  unpublished: new Set(["unpublished", "draft", "ready_for_publishing", "archived"]),
  archived: new Set(["archived", "draft"]),
};

export function isContentStatus(value: unknown): value is ContentStatus {
  return typeof value === "string" && value in CONTENT_STATUS;
}

export function normalizeContentStatus(value: unknown): ContentStatus {
  return isContentStatus(value) ? value : "draft";
}

export function isLiveOnSiteStatus(value: unknown): boolean {
  return isContentStatus(value) && LIVE_ON_SITE_STATUSES.has(value);
}

/**
 * Validate an editor-driven status change.
 * Throws with a clear message on illegal transitions or system-only targets.
 */
export function assertStatusTransition(
  previousStatus: unknown,
  nextStatus: unknown,
): void {
  const from = normalizeContentStatus(previousStatus);
  const to = normalizeContentStatus(nextStatus);

  if (from === to) return;

  if (to === "published" || to === "unpublished") {
    throw new Error(
      `Status "${CONTENT_STATUS[to]}" can only be set by the Publish button ` +
        `(processes Ready for Publishing / Live · Ready → Live and Unpublishing → Unpublished).`,
    );
  }

  if (!EDITOR_SETTABLE_STATUSES.has(to)) {
    throw new Error(`Status "${String(nextStatus)}" is not allowed.`);
  }

  const allowed = ALLOWED_TRANSITIONS[from];
  if (!allowed.has(to)) {
    throw new Error(
      `Cannot change status from "${CONTENT_STATUS[from]}" to "${CONTENT_STATUS[to]}". ` +
        `Allowed next statuses: ${[...allowed]
          .filter((s) => s !== from)
          .map((s) => CONTENT_STATUS[s])
          .join(", ") || "none"}.`,
    );
  }
}
